import { useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Button } from 'components/common';
import { Dropdown } from 'components/common/dropdown';
import { Form, FormGroup, Select, Text } from 'components/common/form';
import { StatCount } from 'components/common/stats';

import type { FormApi } from 'informed';
import type {
  CustomAttributes,
  ProductUserCustomAttribute
} from 'shared-types';

type FormValues = CustomAttributes;

interface Props {
  align?: 'left' | 'right';
  attributes: ProductUserCustomAttribute[];
  clearText?: string;
  onFilterChange?: (attributes: CustomAttributes) => void;
  searchText?: string;
  titleText?: string;
}

const ProductUserCustomAttributeFilters = ({
  align = 'right',
  attributes,
  clearText,
  onFilterChange,
  searchText,
  titleText
}: Props) => {
  const { language, messages, theme } = useReduxState();

  const formApi = useRef<FormApi<FormValues>>(undefined);

  const [customAttributes, setCustomAttributes] =
    useState<CustomAttributes>(null);

  const submit = async () => {
    const { values } = formApi.current.getState();

    const attributeFilters = Object.keys(values)
      .filter((key) => values[key] !== '')
      .reduce((obj, key) => {
        obj[key] = values[key];
        return obj;
      }, {});

    setCustomAttributes(attributeFilters);
    if (onFilterChange) onFilterChange(attributeFilters);
  };

  const handleClear = async () => {
    formApi.current.reset();
    setCustomAttributes(null);
    if (onFilterChange) onFilterChange({});
  };

  const initialValues = attributes.reduce((obj, attr) => {
    obj[attr.id] = customAttributes ? customAttributes[attr.id] || '' : '';
    return obj;
  }, {});

  const numberOfFilters = Object.keys(customAttributes || {}).length;

  return attributes.length ? (
    <div className="filters">
      {numberOfFilters > 0 ? (
        <div className="filters-active">
          <StatCount category="error" value={numberOfFilters} />
        </div>
      ) : null}

      <Dropdown
        align={align}
        category="lighter"
        contentStyle={{
          display: 'flex',
          maxHeight: 'none',
          padding: 0
        }}
        preventDefault={false}
        showArrow={true}
        styleContentButtons={false}
        title={titleText || messages.userAttributes}
      >
        <Form
          allowEmptyStrings={true}
          getApi={(form: FormApi<FormValues>) => (formApi.current = form)}
          initialValues={initialValues}
          onSubmit={submit}
          style={{ display: 'flex' }}
        >
          {({ formState }) => (
            <div className="inner">
              <div className="content" data-keep-open={true}>
                {attributes.map((attr) => {
                  const title =
                    attr.titleTranslations?.[language] || attr.title;

                  const options =
                    attr.type === 'Boolean'
                      ? [
                          { label: messages.commonAll, value: '' },
                          { label: messages.commonYes, value: true },
                          { label: messages.commonNo, value: false },
                          { label: messages.commonNotSet, value: null }
                        ]
                      : attr.type === 'Number'
                      ? [
                          { label: messages.commonAll, value: '' },
                          ...Array.from(
                            Array(Math.max(attr.max - attr.min + 1, 1)),
                            (n, i) => ({
                              label: i + attr.min,
                              value: i + attr.min
                            })
                          )
                        ]
                      : attr.options?.length
                      ? [
                          { label: messages.commonAll, value: '' },
                          ...attr.options.map((option) => ({
                            label: option,
                            value: option
                          }))
                        ]
                      : [];

                  const additionalSelectProps =
                    attr.type === 'Number'
                      ? { isSearchable: false }
                      : attr.options?.length
                      ? { isSearchable: true }
                      : {};

                  return (
                    <FormGroup
                      label={title}
                      key={`product-user-custom-attribute-filter-${attr.id}`}
                    >
                      {options.length ? (
                        <Select
                          error={formState.errors[attr.id]}
                          field={`${attr.id}`}
                          id={attr.id}
                          keepContainerOpen={true}
                          options={options}
                          usePortal={true}
                          {...additionalSelectProps}
                        />
                      ) : (
                        <Text
                          error={formState.errors[attr.id]}
                          field={`${attr.id}`}
                          id={attr.id}
                          type="text"
                        />
                      )}
                    </FormGroup>
                  );
                })}
              </div>

              <div className="buttons">
                <Button
                  category="light"
                  onClick={handleClear}
                  style={{ margin: 5 }}
                  text={clearText || messages.usersFiltersClear}
                />
                <Button
                  category="success"
                  disabled={formState.invalid}
                  onClick={submit}
                  style={{ margin: 5 }}
                  text={searchText || messages.usersFiltersApply}
                  type="submit"
                />
              </div>
            </div>
          )}
        </Form>
      </Dropdown>

      <style jsx>{`
        .filters {
          display: inline-block;
          text-align: left;
          position: relative;
        }
        .filters-active {
          position: absolute;
          top: -12px;
          right: -5px;
          z-index: 1;
        }
        .inner {
          display: flex;
          flex-direction: column;
        }
        .content {
          padding: 15px 15px;
          max-height: 400px;
          overflow: auto;
        }
        .buttons {
          border-top: 1px solid ${theme.lightGrey};
          padding: 5px 10px 5px;
          text-align: right;
        }
        .filters :global(.dropdown-content) {
          width: 260px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .filters :global(.dropdown-content) {
            width: 320px;
          }
        }
      `}</style>
    </div>
  ) : null;
};

export default ProductUserCustomAttributeFilters;
